/**
 * Copy for the shared auth forms.
 *
 * ## Why this is injectable instead of translated in place
 *
 * These forms are rendered by three apps with three different audiences:
 *
 *   - `apps/app`    — the learner app, translated (en / vi)
 *   - `apps/admin`  — internal staff tooling, English only
 *   - `apps/www`    — the marketing site, its own locale story
 *
 * Hard-coding Vietnamese here, or importing i18next into `@lyder-talk/ui`,
 * would drag the admin panel along with it: an admin opening `/admin/signin`
 * would get Vietnamese labels because someone translated a *learner* surface.
 * The shared package therefore stays locale-agnostic and English-defaulted,
 * and each app decides what to pass in.
 *
 * The `variant="admin"` branch inside each form is deliberately left out of
 * these types — the admin copy is English-only by policy, and adding keys for
 * it would invite someone to translate it later.
 *
 * Every field is optional and merged over the English default, so adding a
 * string is backwards-compatible and a caller can override one line without
 * restating the form.
 */

export interface SigninFormCopy {
  title: string;
  description: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  /** `aria-label` for the reveal toggle, in both states. */
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  forgotPassword: string;
  submitting: string;
  submit: string;
  continueWithGoogle: string;
  newHere: string;
  createAccount: string;
  legal?: Partial<AuthLegalCopy>;
  genericError: string;
}

export interface SignupFormCopy {
  /** Password-requirement tick labels, in `passwordRules` order. */
  passwordRuleLabels?: string[];
  /** `aria-label` for the reveal toggle, in both states. */
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  /**
   * Meter captions, in ascending score order. Index 0 is the empty state,
   * so pass four entries for the four filled bars.
   */
  passwordStrengthLabels?: string[];
  successTitle: string;
  successDescription: string;
  successTitleConfirmEmail: string;
  successDescriptionConfirmEmail: string;
  /** The three "what happens next" steps on the success panel, in order. */
  steps: [string, string, string];
  successActionConfirmEmail: string;
  successAction: string;
  benefitOne: string;
  title: string;
  description: string;
  displayNameLabel: string;
  displayNamePlaceholder: string;
  emailLabel: string;
  passwordLabel: string;
  confirmPasswordLabel: string;
  matchHint: string;
  creating: string;
  submit: string;
  googleLabel: string;
  passwordTooShort: string;
  passwordsMismatch: string;
  unexpectedError: string;
  haveAccount: string;
  signInInstead: string;
  legal?: Partial<AuthLegalCopy>;
}

export interface ForgotPasswordFormCopy {
  title: string;
  description: string;
  emailLabel: string;
  sending: string;
  submit: string;
  backToSignIn: string;
  sentTitle: string;
  /**
   * The "check your inbox" confirmation. A function because it has to name the
   * address that was submitted, and this package has no interpolator to lean on
   * — splitting a `{{email}}` placeholder inside the JSX would hide the
   * dependency in a place nobody looks. The rendered form emails it as its own
   * styled `<span>`.
   */
  sentDescription: (email: string) => string;
  tryAnotherEmail: string;
  sendFailed: string;
}

export interface ResetPasswordFormCopy {
  /** Password-requirement tick labels, in `passwordRules` order. */
  passwordRuleLabels?: string[];
  /**
   * Meter captions, in ascending score order. Index 0 is the empty state,
   * so pass four entries for the four filled bars.
   */
  passwordStrengthLabels?: string[];
  /** `aria-label` for the reveal toggle, in both states. */
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
  title: string;
  description: string;
  passwordLabel: string;
  confirmPasswordLabel: string;
  matchHint: string;
  submit: string;
  submitting: string;
  backToSignIn: string;
  tooShort: string;
  passwordsMismatch: string;
  failed: string;
}

/**
 * The sign-up legal footnote. Shared with `SigninFormCopy` rather than
 * duplicated into both — it is the same sentence, and two copies of a legal
 * line is how the two forms end up disagreeing about what users agreed to.
 */
export interface AuthLegalCopy {
  termsPrefix: string;
  termsLink: string;
  andPrefix: string;
  privacyLink: string;
  /** Divider label above the social buttons. */
  orContinueWith: string;
}

export const DEFAULT_AUTH_LEGAL_COPY: AuthLegalCopy = {
  termsPrefix: "By continuing you agree to our",
  termsLink: "Terms",
  andPrefix: "and",
  privacyLink: "Privacy Policy",
  orContinueWith: "or continue with",
};

/** The English text every app gets unless it overrides it. */
export const DEFAULT_SIGNIN_COPY: SigninFormCopy = {
  title: "Welcome back",
  description: "Pick up your streak where you left it.",
  emailLabel: "Email address",
  emailPlaceholder: "you@example.com",
  passwordLabel: "Password",
  forgotPassword: "Forgot password?",
  submitting: "Signing you in…",
  submit: "Sign in",
  continueWithGoogle: "Continue with Google",
  newHere: "New here?",
  createAccount: "Create an account",
  genericError: "An error occurred. Please try again.",
};

export const DEFAULT_SIGNUP_COPY: SignupFormCopy = {
  successTitle: "You're all set",
  successDescription: "Your desk is ready. Head in and build your first topic.",
  successTitleConfirmEmail: "Confirm your email",
  successDescriptionConfirmEmail: "Confirm the email we just sent",
  steps: [
    "Sign in with your new account",
    "Generate your first speaking topic",
    "Save words you meet while you talk",
  ],
  successActionConfirmEmail: "Go to sign in",
  successAction: "Start learning",
  benefitOne: "Save words you meet while you talk",
  title: "Create your account",
  description: "Free to start. Your progress and saved words stay with you.",
  displayNameLabel: "Display name",
  displayNamePlaceholder: "How should we greet you?",
  emailLabel: "Email address",
  passwordLabel: "Password",
  confirmPasswordLabel: "Confirm password",
  matchHint: "Both entries must match.",
  creating: "Creating your account…",
  submit: "Create account",
  googleLabel: "Sign up with Google",
  passwordTooShort: "Password must be at least 8 characters.",
  passwordsMismatch: "Passwords do not match.",
  unexpectedError: "An unexpected error occurred. Please try again.",
  haveAccount: "Already have an account?",
  signInInstead: "Sign in",
};

export const DEFAULT_FORGOT_PASSWORD_COPY: ForgotPasswordFormCopy = {
  title: "Reset your password",
  description: "Enter your email and we'll send you a link to choose a new one.",
  emailLabel: "Email address",
  sending: "Sending the link…",
  submit: "Send reset link",
  backToSignIn: "Back to sign in",
  sentTitle: "Check your inbox",
  sentDescription: (email) =>
    `If an account exists for ${email}, a reset link is on its way — it expires in one hour.`,
  tryAnotherEmail: "Try another email",
  sendFailed: "Failed to send reset email. Please try again.",
};

export const DEFAULT_RESET_PASSWORD_COPY: ResetPasswordFormCopy = {
  title: "Create a new password",
  description: "Choose something you will remember but nobody would guess.",
  passwordLabel: "New password",
  confirmPasswordLabel: "Confirm new password",
  matchHint: "Both entries must match.",
  submit: "Reset password",
  submitting: "Updating password…",
  backToSignIn: "Back to sign in",
  tooShort: "Password must be at least 6 characters long.",
  passwordsMismatch: "Passwords do not match.",
  failed: "Failed to reset password. Please try again.",
};
